import { parsePhoneNumber, isValidPhoneNumber, getCountryCallingCode } from "libphonenumber-js";

/**
 * Validates a phone number, optionally with a default country code.
 * @param {string} phone
 * @param {string} defaultCountry - ISO 3166 country code (e.g., "US", "BR")
 * @returns {boolean}
 */
export function isValidPhone(phone, defaultCountry = null) {
  if (!phone || typeof phone !== "string") return false;
  const trimmed = phone.trim();
  if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) return true;
  return isValidPhoneNumber(trimmed);
}

/**
 * Formats a phone number to international display format (e.g., +1 212 555 1234).
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {string|null}
 */
export function formatPhoneInternational(phone, defaultCountry = null) {
  try {
    if (!phone || typeof phone !== "string") return null;
    const trimmed = phone.trim();
    if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) {
      return parsePhoneNumber(trimmed, defaultCountry).formatInternational();
    }
    if (isValidPhoneNumber(trimmed)) {
      return parsePhoneNumber(trimmed).formatInternational();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parses a phone number and returns structured info.
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {object|null} - { number, country, nationalNumber, countryCallingCode, isValid, formatted }
 */
export function parsePhone(phone, defaultCountry = null) {
  try {
    if (!phone || typeof phone !== "string") return null;
    const trimmed = phone.trim();

    let parsed = null;
    if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) {
      parsed = parsePhoneNumber(trimmed, defaultCountry);
    } else if (isValidPhoneNumber(trimmed)) {
      parsed = parsePhoneNumber(trimmed);
    } else if (!trimmed.startsWith("+")) {
      const withPlus = "+" + trimmed;
      if (isValidPhoneNumber(withPlus)) {
        parsed = parsePhoneNumber(withPlus);
      }
    }

    if (!parsed) return null;

    return {
      number: parsed.number,
      country: parsed.country,
      nationalNumber: parsed.nationalNumber,
      countryCallingCode: parsed.countryCallingCode,
      isValid: true,
      formatted: parsed.formatInternational(),
    };
  } catch {
    return null;
  }
}

/**
 * Normalizes a phone number for storage. Tries to format to E.164.
 * If valid, returns the E.164 format. If not, returns cleaned input.
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {{ phone: string|null, isValidated: boolean }}
 */
/**
 * Gets the calling code for a country (e.g., "US" → "1", "BR" → "55").
 * @param {string} countryCode - ISO 3166 country code
 * @returns {string|null}
 */
export function getCallingCode(countryCode) {
  try {
    if (!countryCode) return null;
    return getCountryCallingCode(countryCode.toUpperCase());
  } catch {
    return null;
  }
}

export function normalizePhone(phone, defaultCountry = null) {
  if (!phone || typeof phone !== "string") return { phone: null, isValidated: false };
  const trimmed = phone.trim();
  if (!trimmed) return { phone: null, isValidated: false };

  const parsed = parsePhone(trimmed, defaultCountry);
  if (parsed) {
    return { phone: parsed.number, isValidated: true };
  }
  return { phone: trimmed, isValidated: false };
}
